// Styled offer workbook. ExcelJS is already a client-side dependency (the
// packing-list template needs it for fonts, borders and images). SheetJS
// community edition cannot style cells or embed the logo.
import ExcelJS from 'exceljs';
import { EXCEL_LOGO_ROW_POINTS, excelLogoSize, prepareExcelLogo } from './excelLogo';

const FONT = 'Calibri';

// Main-sheet palette. The Bank Accounts sheet still uses the previous navy.
const BRAND = 'FF1F3A6E';
const TEXT = 'FF1F2937';
const WHITE = 'FFFFFFFF';
const GRID = 'FFC9D6E8';
const NAVY = 'FF2C3E5A';
const PRIMARY = 'FF2563EB';
const MUTED = 'FF64748B';
const BAND = 'FFF8FAFC';

const thin = { style: 'thin', color: { argb: 'FF94A3B8' } };
const thick = { style: 'medium', color: { argb: NAVY } };
const THIN_GRID = { style: 'thin', color: { argb: GRID } };
const BOX_EDGE = { style: 'medium', color: { argb: 'FF000000' } };
const BRAND_EDGE = { style: 'medium', color: { argb: BRAND } };
// D is wide enough for RECONDITIONED at 10pt. E gives the width back.
const WIDE_WIDTHS = [6, 35.83, 14.16, 14.5, 17.66, 6.66, 15.33, 19.83];
const COMPACT_WIDTHS = [16, 46, 16, 24, 24];
const EMU_PER_PIXEL = 9525;
const EMU_PER_POINT = 12700;

const fill = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });

const baseFont = (overrides = {}) => ({
  name: FONT,
  size: 11,
  color: { argb: TEXT },
  ...overrides
});

const currencyFormat = (currency) => {
  const code = String(currency || 'USD').replace(/"/g, '');
  return `"${code} "#,##0.00`;
};

// The screen and PDF show unit prices at 2 decimals (385.73, not 385.726).
// Four decimals are only for a price that is itself under one cent.
const unitPriceFormat = (currency, value) => {
  const amount = Math.abs(Number(value) || 0);
  if (amount > 0 && amount < 0.01) {
    const code = String(currency || 'USD').replace(/"/g, '');
    return `"${code} "#,##0.00##`;
  }
  return currencyFormat(currency);
};

const PRICED_TYPES = ['OFFER', 'PROFORMA INVOICE', 'INVOICE', 'COMMERCIAL INVOICE'];

// Legacy rows stored a blank type. Those are offers. Every comparison in the
// export uses this form so " inquiry " and "INQUIRY" take the same path.
export const normalizeOfferType = (offerType) => {
  const normalized = String(offerType || '').trim().toUpperCase();
  return normalized || 'OFFER';
};

const roundToDigits = (value, digits) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 0;
  const sign = amount < 0 ? -1 : 1;
  const factor = 10 ** digits;
  return sign * (Math.round((Math.abs(amount) + 1e-10) * factor) / factor);
};

const roundMoney = (value) => roundToDigits(value, 2);

// Screen and PDF format an unrounded amount with toLocaleString (2 fraction
// digits): the discount via formatPrice(calculateDiscountAmount) and FINAL
// via formatPrice(getFinalTotal()). Excel stores those displayed figures.
// roundMoney of the same remainder can differ by a cent (100.005 paid), and
// the displayed subtotal minus the displayed FINAL is not that discount.
const roundDisplayedMoney = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 0;
  const text = amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  const parsed = Number(String(text).replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

// A price of one cent or more is stored the way the screen and PDF show it.
// Rounding to 4 decimals and then displaying 2 digits is one cent high for
// 687.454975 and 1.00495. Only a price below one cent keeps 4 decimals.
const storedUnitPrice = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 0;
  if (Math.abs(amount) > 0 && Math.abs(amount) < 0.01) return roundToDigits(amount, 4);
  return roundDisplayedMoney(amount);
};

// Integers use a plain integer format. `#,##0.##` draws a trailing dot in
// Excel ("6."). Fractional quantities keep a general format so the decimals
// that are actually there still show.
const quantityFormat = (qty) => {
  const amount = Number(qty);
  if (Number.isFinite(amount) && !Number.isInteger(amount)) return 'General';
  return '0';
};

// User text is always a shared string, never a formula object. Excel then
// shows a leading = + - @ as text and does not execute it.
const setCellText = (cell, value) => {
  if (value && typeof value === 'object' && Array.isArray(value.richText)) {
    cell.value = {
      richText: value.richText.map((part) => ({
        ...part,
        text: String(part.text ?? '')
      }))
    };
    cell.numFmt = '@';
    return;
  }
  if (typeof value === 'number' || typeof value === 'boolean' || value == null) {
    cell.value = value;
    return;
  }
  cell.value = String(value);
  cell.numFmt = '@';
};

const formatDocumentDate = (iso) => {
  if (!iso) return '';
  const [year, month, day] = String(iso).split('-');
  if (!year || !month || !day) return String(iso);
  return `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
};

const validUntil = (iso) => {
  const [year, month, day] = String(iso || '').split('-').map(Number);
  if (!year || !month || !day) return '';
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + 14);
  const d = String(date.getUTCDate()).padStart(2, '0');
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${d}.${m}.${date.getUTCFullYear()}`;
};

const partyLabel = (offerType) => {
  const type = normalizeOfferType(offerType);
  return type === 'PURCHASE ORDER' || type === 'INQUIRY' ? 'SUPPLIER' : 'CUSTOMER';
};

const sheetNameFor = (offerType) => ({
  OFFER: 'Offer',
  'PROFORMA INVOICE': 'Proforma Invoice',
  INVOICE: 'Invoice',
  'COMMERCIAL INVOICE': 'Commercial Invoice',
  'PURCHASE ORDER': 'Purchase Order',
  INQUIRY: 'Inquiry',
  'PACKING LIST': 'Packing List'
}[normalizeOfferType(offerType)] || 'Document');

// Offer number, description, customer, and document type are user text in
// the download name. Keep Unicode letters and numbers. Strip controls, the
// Unicode format category (bidi marks, zero-width characters), path pieces,
// Windows device names, and trailing dots or spaces. Cap each part.
const WINDOWS_RESERVED = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i;
const FILENAME_PART_MAX = 80;

const isControlCode = (code) => code < 32 || (code >= 127 && code <= 159);

// Cf, including U+200B–U+200F, U+202A–U+202E and U+2066–U+2069.
const isFormatChar = (char) => /\p{Cf}/u.test(char);

const isLoneSurrogate = (code) => code >= 0xD800 && code <= 0xDFFF;

const isCombiningMark = (code) => /\p{M}/u.test(String.fromCodePoint(code));

// Cell text keeps line breaks. CRLF and CR become LF, line and paragraph
// separators become LF, and a tab becomes a space. Other controls, Cf
// (bidi marks and zero-width characters) and lone surrogates are removed.
const visibleCellText = (value) => {
  const normalized = String(value ?? '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\u2028/g, '\n')
    .replace(/\u2029/g, '\n')
    .replace(/\t/g, ' ');
  let out = '';
  for (const char of normalized) {
    const code = char.codePointAt(0);
    if (code === 0x0A) {
      out += '\n';
      continue;
    }
    // ZWJ and ZWNJ join emoji, Persian and Indic letters. They stay in cells.
    if (code === 0x200C || code === 0x200D) {
      out += char;
      continue;
    }
    if (isControlCode(code) || isFormatChar(char) || isLoneSurrogate(code)) continue;
    out += char;
  }
  return out;
};

// File names cannot hold a line break. Turn breaks and tabs into spaces
// before words are split, then drop the remaining hidden characters.
const stripFilenameChars = (value) => {
  const spaced = String(value ?? '')
    .replace(/\r\n/g, ' ')
    .replace(/[\n\r\t\u2028\u2029]/g, ' ');
  let out = '';
  for (const char of spaced) {
    const code = char.codePointAt(0);
    if (isControlCode(code) || isFormatChar(char) || isLoneSurrogate(code)) continue;
    out += char;
  }
  return out;
};

const graphemeClusters = (value) => {
  const text = String(value);
  if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
    return Array.from(
      new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(text),
      (part) => part.segment
    );
  }
  const clusters = [];
  let current = '';
  for (const char of text) {
    const code = char.codePointAt(0);
    if (current && !isCombiningMark(code)) {
      clusters.push(current);
      current = '';
    }
    current += char;
  }
  if (current) clusters.push(current);
  return clusters;
};

const capFilenamePart = (value) => {
  const clusters = graphemeClusters(value);
  if (clusters.length <= FILENAME_PART_MAX) return value;
  return clusters.slice(0, FILENAME_PART_MAX).join('').replace(/[-_. ]+$/u, '');
};

const sanitizeFilenamePart = (value, fallback) => {
  let text = stripFilenameChars(value);
  text = text.replace(/[/\\]/g, '');
  while (text.includes('..')) text = text.replace(/\.\./g, '');
  text = text.replace(/[<>:"|?*]/g, '');
  text = text.replace(/-+/g, '-').replace(/[._ ]+$/g, '').replace(/^-+|-+$/g, '').trim();
  text = capFilenamePart(text);
  if (!text || WINDOWS_RESERVED.test(text)) return fallback;
  return text;
};

const slugFilenamePart = (value, fallback) => {
  let text = stripFilenameChars(value);
  text = text.replace(/[/\\]/g, '');
  while (text.includes('..')) text = text.replace(/\.\./g, '');
  text = text.replace(/[<>:"|?*]/g, '');
  text = text.replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-+|-+$/g, '');
  text = capFilenamePart(text);
  if (!text || WINDOWS_RESERVED.test(text)) return fallback;
  return text;
};

const FILENAME_MAX_BYTES = 200;
const FILENAME_TAIL_MAX_BYTES = 100;

// UTF-8 byte length of one code point. A 4-byte letter is kept whole or left out.
const utf8Bytes = (char) => {
  const code = char.codePointAt(0);
  if (code <= 0x7F) return 1;
  if (code <= 0x7FF) return 2;
  if (code <= 0xFFFF) return 3;
  return 4;
};

const utf8Length = (value) => {
  let size = 0;
  for (const char of String(value)) size += utf8Bytes(char);
  return size;
};

const cutToBytes = (value, budget) => {
  if (budget <= 0) return '';
  let out = '';
  let used = 0;
  for (const cluster of graphemeClusters(value)) {
    const size = utf8Length(cluster);
    if (used + size > budget) break;
    out += cluster;
    used += size;
  }
  return out.replace(/[-_. ]+$/u, '');
};

const capWholeFilename = (filename) => {
  const extension = '.xlsx';
  if (utf8Length(filename) <= FILENAME_MAX_BYTES) return filename;
  const stem = filename.endsWith(extension) ? filename.slice(0, -extension.length) : filename;
  return `${cutToBytes(stem, FILENAME_MAX_BYTES - utf8Length(extension))}${extension}`;
};

export const buildExcelFilename = (model) => {
  const companyShort = model.companyKey === 'fin-marine' ? 'FIN-MARINE'
    : model.companyKey === 'fin-marine-fzco' ? 'FIN-MARINE-FZCO' : 'ATLANTIS';
  const addressIndicators = ['street', 'road', 'avenue', 'building', 'floor', 'suite', 'office'];
  // "unit" is an address word only when a number follows ("Unit 5"), so a
  // customer such as "Unit Marine Ltd" is kept.
  const isAddressWord = (words, index) => {
    const lower = String(words[index] || '').toLowerCase();
    if (addressIndicators.some((indicator) => lower.includes(indicator))) return true;
    return lower.includes('unit') && /^\d/.test(String(words[index + 1] || ''));
  };
  const customerWords = stripFilenameChars(model.customer?.name || '')
    .split(/[\s\n,]+/)
    .filter((word) => word.trim())
    .slice(0, 5)
    .filter((word, index, arr) => {
      if (!word || isAddressWord(arr, index)) return false;
      for (let earlier = 0; earlier < index; earlier += 1) {
        if (isAddressWord(arr, earlier)) return false;
      }
      return true;
    })
    .slice(0, 4)
    .map((word) => word.replace(/[^\p{L}\p{N}_]+/gu, ''))
    .filter(Boolean);
  const customerClean = capFilenamePart(customerWords.join('_').replace(/^_|_$/g, '')) || 'Customer';
  const forClean = slugFilenamePart(model.description || '', 'Equipment');
  const docType = sanitizeFilenamePart(normalizeOfferType(model.offerType).replace(/\s+/g, '-'), 'Document');
  const offerNumber = sanitizeFilenamePart(model.offerNumber, 'Document');
  // The number and type share 100 bytes. Description and customer shrink
  // first. The finished name, including .xlsx, stays within 200 bytes.
  const typePart = cutToBytes(docType, FILENAME_TAIL_MAX_BYTES);
  const numberPart = cutToBytes(offerNumber, FILENAME_TAIL_MAX_BYTES - utf8Length(typePart) - 1);
  const offerTail = `${numberPart}-${typePart}`.replace(/-+/g, '-').replace(/^-|-$/g, '');
  const prefix = `${companyShort}-For-`;
  const extension = '.xlsx';
  const flexible = cutToBytes(
    [forClean, customerClean].filter(Boolean).join('-'),
    FILENAME_MAX_BYTES - utf8Length(prefix) - utf8Length(offerTail) - utf8Length(extension) - 1
  );
  const filename = `${prefix}${flexible}-${offerTail}${extension}`.replace(/-{2,}/g, '-');
  return capWholeFilename(filename);
};

const customerBlock = (customer, offerType) => {
  const lines = [];
  const visible = (value) => visibleCellText(value).trim();
  const nameLines = visible(customer?.name)
    .split(/\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  lines.push(`${partyLabel(offerType)}: ${nameLines[0] || ''}`);
  nameLines.slice(1).forEach((line) => lines.push(line));
  [
    ['Street', customer?.street || customer?.address],
    ['City', customer?.city],
    ['State/province/area', customer?.state || customer?.province || customer?.stateProvince],
    ['Phone number', customer?.phone || customer?.phoneNumber],
    ['Contact person', customer?.contact],
    ['Zip code', customer?.zip || customer?.zipCode || customer?.postalCode],
    ['Country', customer?.country],
    ['Project', customer?.project]
  ].forEach(([label, value]) => {
    const text = visible(value);
    if (text) lines.push(`${label}: ${text}`);
  });
  return lines.join('\n');
};

// Priced documents and purchase orders put part no, condition and remarks in
// their own columns. COO and make stay in the description. Inquiry and the
// packing list keep the stacked description.
const detailColumns = (offerType) => {
  const type = normalizeOfferType(offerType);
  return type !== 'INQUIRY' && type !== 'PACKING LIST';
};

const layoutFor = (offerType) => {
  const wide = detailColumns(offerType);
  return {
    wide,
    cols: wide ? 8 : 5,
    widths: wide ? WIDE_WIDTHS : COMPACT_WIDTHS
  };
};

const itemDescription = (item, offerType) => {
  const type = normalizeOfferType(offerType);
  const lines = [];
  const visible = (value) => visibleCellText(value);
  const description = visible(item.description);
  if (description) lines.push(description);
  if (!detailColumns(type)) {
    const partNo = visible(item.partNo);
    if (partNo) lines.push(`Part No: ${partNo}`);
    const condition = visible(item.condition);
    if (condition) lines.push(`Condition: ${condition}`);
  }
  const coo = visible(item.coo);
  if (coo) lines.push(`COO: ${coo}`);
  const make = visible(item.make);
  if (make) lines.push(`Make: ${make}`);
  if (!detailColumns(type)) {
    const remarks = visible(item.remarks);
    if (remarks && type !== 'INQUIRY') lines.push(`Remarks: ${remarks}`);
  }
  if (type !== 'PACKING LIST' && type !== 'INQUIRY') {
    const availability = visible(item.availability);
    if (availability) lines.push(`Availability: ${availability}`);
  }
  return lines.join('\n');
};

const termsText = (terms, offerType) => {
  if (normalizeOfferType(offerType) === 'INQUIRY') {
    return 'TERMS:\nPlease include your terms and conditions, including delivery time, best price, and payment terms.';
  }
  const entries = (value) => {
    const list = Array.isArray(value) ? value : value == null ? [] : [value];
    return list.map((entry) => visibleCellText(entry).trim()).filter(Boolean);
  };
  const lines = ['TERMS:'];
  [
    ['DELIVERY TERMS:', entries(terms?.deliveryTerms)],
    ['DELIVERY TIME:', entries(terms?.deliveryTime)],
    ['PAYMENT TERMS:', entries(terms?.paymentTerms)]
  ].forEach(([label, values]) => {
    if (!values.length) {
      lines.push(label);
      return;
    }
    values.forEach((value, index) => {
      lines.push(index === 0 ? `${label} ${value}` : value);
    });
  });
  return lines.join('\n');
};

const accountLines = (currency, details = {}) => {
  const lines = [`${currency} Account`];
  [
    ['Holder:', details.holder],
    ['Bank:', details.bank],
    ['Branch:', details.branch],
    ['Branch code:', details.branchCode],
    ['Account no:', details.accountNo],
    ['IBAN:', details.iban],
    ['SWIFT:', details.swiftCode],
    ['Routing:', details.routingCode]
  ].forEach(([label, value]) => {
    const text = String(value || '').trim();
    if (text) lines.push(`${label} ${text}`);
  });
  return lines.join('\n');
};

const applyFrame = (sheet, top, left, bottom, right) => {
  for (let row = top; row <= bottom; row += 1) {
    for (let col = left; col <= right; col += 1) {
      sheet.getCell(row, col).border = {
        top: row === top ? thick : thin,
        bottom: row === bottom ? thick : thin,
        left: col === left ? thick : thin,
        right: col === right ? thick : thin
      };
    }
  }
};

// Outer medium edges only. A merged row shares one style object, so the
// border has to be written on the master cell and nowhere else.
const applyBox = (sheet, top, left, bottom, right, edge = BOX_EDGE) => {
  if (top === bottom) {
    sheet.getCell(top, left).border = {
      top: edge,
      bottom: edge,
      left: edge,
      right: edge
    };
    return;
  }
  for (let row = top; row <= bottom; row += 1) {
    for (let col = left; col <= right; col += 1) {
      const border = {};
      if (row === top) border.top = edge;
      if (row === bottom) border.bottom = edge;
      if (col === left) border.left = edge;
      if (col === right) border.right = edge;
      if (border.top || border.bottom || border.left || border.right) {
        sheet.getCell(row, col).border = border;
      }
    }
  }
};

// Customer/date and FOR/serial are two merges with no line between them.
const applyOpenRow = (sheet, row, rightStart) => {
  sheet.getCell(row, 1).border = { top: BOX_EDGE, bottom: BOX_EDGE, left: BOX_EDGE };
  sheet.getCell(row, rightStart).border = {
    top: BOX_EDGE,
    bottom: BOX_EDGE,
    right: BOX_EDGE
  };
};

const fillRow = (sheet, row, cols, argb) => {
  for (let col = 1; col <= cols; col += 1) sheet.getCell(row, col).fill = fill(argb);
};

// Item table: medium brand sides, thin grid inside, medium black along the top.
const applyItemGrid = (sheet, top, bottom, cols) => {
  for (let row = top; row <= bottom; row += 1) {
    for (let col = 1; col <= cols; col += 1) {
      const border = {
        top: row === top ? BOX_EDGE : THIN_GRID,
        left: col === 1 ? BRAND_EDGE : THIN_GRID
      };
      if (row === bottom) border.bottom = THIN_GRID;
      if (col === cols) border.right = BRAND_EDGE;
      sheet.getCell(row, col).border = border;
    }
  }
};

const columnPixels = (width) => Math.trunc(((256 * width + Math.trunc(128 / 7)) / 256) * 7);

// Calibri 11 advances in pixels at 96 DPI (Carlito is metric-compatible),
// ASCII 32–126. Anything else uses the widest glyph so a long token is not cut.
const CALIBRI_11_PX = [
  3.31, 4.77, 5.88, 7.3, 7.42, 10.48, 10, 3.23, 4.44, 4.44, 7.3, 7.3, 3.66, 4.48, 3.7, 5.66,
  7.42, 7.42, 7.42, 7.42, 7.42, 7.42, 7.42, 7.42, 7.42, 7.42, 3.92, 3.92, 7.3, 7.3, 7.3, 6.8,
  13.11, 8.48, 7.97, 7.81, 9.02, 7.16, 6.73, 9.25, 9.12, 3.69, 4.67, 7.61, 6.16, 12.53, 9.45, 9.7,
  7.58, 9.86, 7.95, 6.73, 7.14, 9.41, 8.31, 13.03, 7.61, 7.14, 6.86, 4.5, 5.66, 4.5, 7.3, 7.3,
  4.27, 7.02, 7.7, 6.2, 7.7, 7.3, 4.47, 6.91, 7.7, 3.36, 3.5, 6.66, 3.36, 11.7, 7.7, 7.73,
  7.7, 7.7, 5.11, 5.73, 4.91, 7.7, 6.62, 10.48, 6.34, 6.64, 5.8, 4.61, 6.75, 4.61, 7.3
];
const CALIBRI_11_MAX_PX = 13.11;

const charWidthPx = (char, fontSize) => {
  const code = char.codePointAt(0);
  const at11 = code >= 32 && code <= 126 ? CALIBRI_11_PX[code - 32] : CALIBRI_11_MAX_PX;
  return at11 * (fontSize / 11);
};

const textWidthPx = (text, fontSize) => {
  let width = 0;
  for (const char of text) width += charWidthPx(char, fontSize);
  return width;
};

// Excel's usable text width is the column pixel width minus 5px of padding.
// A few more pixels keep the last glyph off the border.
const spanTextPixels = (fromCol, toCol, widths) => {
  const pixels = widths.slice(fromCol - 1, toCol).reduce((sum, width) => sum + columnPixels(width), 0);
  return Math.max(1, pixels - 8);
};

const lineCountFor = (line, fontSize, limit) => {
  if (!line) return 1;
  let lines = 1;
  let used = 0;
  line.split(/(\s+)/).forEach((part) => {
    if (!part) return;
    const space = /^\s+$/.test(part);
    const partWidth = textWidthPx(part, fontSize);
    if (!space && partWidth > limit) {
      if (used > 0) {
        lines += 1;
        used = 0;
      }
      for (const char of part) {
        const width = charWidthPx(char, fontSize);
        if (used > 0 && used + width > limit) {
          lines += 1;
          used = width;
        } else {
          used += width;
        }
      }
      return;
    }
    if (used > 0 && used + partWidth > limit) {
      lines += 1;
      used = space ? 0 : partWidth;
      return;
    }
    used += partWidth;
  });
  return lines;
};

const wrappedLineCount = (text, fontSize, pixels) => String(text || '').split('\n').reduce((sum, line) => (
  sum + lineCountFor(line, fontSize, pixels)
), 0);

// Excel's default row is 15pt for Calibri 11. Scale that, then add a little
// so vertically centred text is not clipped.
const EXCEL_MAX_ROW_POINTS = 409;

const textHeight = (text, fromCol, toCol, fontSize, minimum, widths) => {
  const lines = wrappedLineCount(text, fontSize, spanTextPixels(fromCol, toCol, widths));
  return Math.min(EXCEL_MAX_ROW_POINTS, Math.max(minimum, Math.ceil((lines * fontSize * 15) / 11 + 4)));
};

const box = (sheet, row, text, {
  fillColor,
  font,
  horizontal = 'left',
  vertical = 'middle',
  indent = 0,
  span,
  widths,
  framed = true,
  minHeight = 22
} = {}) => {
  sheet.mergeCells(row, 1, row, span);
  const cell = sheet.getCell(row, 1);
  setCellText(cell, text);
  cell.font = font || baseFont();
  const alignment = { horizontal, vertical, wrapText: true };
  if (indent) alignment.indent = indent;
  cell.alignment = alignment;
  if (fillColor) fillRow(sheet, row, span, fillColor);
  if (framed) applyBox(sheet, row, 1, row, span);
  const fontSize = (font && font.size) || 11;
  const plain = text && typeof text === 'object' ? '' : text;
  sheet.getRow(row).height = textHeight(plain, 1, span, fontSize, minHeight, widths);
  return row + 1;
};

// ExcelJS stores a fractional column offset as width*10000, but the xlsx
// anchor is EMUs. LibreOffice then draws the logo against the left of the
// column. Place it with a real EMU offset measured from the column widths.
const placeLogo = (workbook, sheet, base64, widths) => {
  const prepared = prepareExcelLogo(base64);
  const logo = excelLogoSize(prepared.width, prepared.height);
  const rowPoints = EXCEL_LOGO_ROW_POINTS;
  sheet.getRow(1).height = rowPoints;
  const imageId = workbook.addImage({ base64: prepared.base64, extension: 'png' });
  const columnPx = widths.map(columnPixels);
  const total = columnPx.reduce((sum, value) => sum + value, 0);
  let offset = Math.max(0, Math.round((total - logo.width) / 2));
  let col = 0;
  while (col < columnPx.length - 1 && offset >= columnPx[col]) {
    offset -= columnPx[col];
    col += 1;
  }
  const rowEmu = rowPoints * EMU_PER_POINT;
  const imageEmu = logo.height * EMU_PER_PIXEL;
  sheet.addImage(imageId, {
    tl: {
      nativeCol: col,
      nativeColOff: offset * EMU_PER_PIXEL,
      nativeRow: 0,
      nativeRowOff: Math.max(0, Math.round((rowEmu - imageEmu) / 2))
    },
    ext: { width: logo.width, height: logo.height },
    editAs: 'oneCell'
  });
};

const applyPrint = (sheet, headerRow, options = {}) => {
  sheet.pageSetup.paperSize = 9;
  sheet.pageSetup.orientation = options.orientation || 'portrait';
  sheet.pageSetup.horizontalCentered = true;
  if (options.scale) {
    // A fixed scale is the owner's 75%. fitToPage would ignore it.
    sheet.pageSetup.fitToPage = false;
    sheet.pageSetup.fitToWidth = undefined;
    sheet.pageSetup.fitToHeight = undefined;
    sheet.pageSetup.scale = options.scale;
  } else {
    sheet.pageSetup.fitToPage = true;
    sheet.pageSetup.fitToWidth = 1;
    sheet.pageSetup.fitToHeight = options.fitToHeight == null ? 0 : options.fitToHeight;
    sheet.pageSetup.scale = undefined;
  }
  sheet.pageSetup.margins = {
    left: 0.7,
    right: 0.7,
    top: 0.75,
    bottom: 0.75,
    header: 0.3,
    footer: 0.3
  };
  if (headerRow && options.repeatHeader !== false) {
    sheet.pageSetup.printTitlesRow = `${headerRow}:${headerRow}`;
  }
  const view = { showGridLines: false, state: 'normal' };
  if (options.zoom) view.zoomScale = options.zoom;
  sheet.views = [view];
  sheet.pageSetup.paperSize = 9;
  // &C centres the footer. &P / &N are Excel's page and page-count fields.
  const pageFooter = '&CPage &P of &N';
  sheet.headerFooter.oddFooter = pageFooter;
  sheet.headerFooter.evenFooter = pageFooter;
};

const DEFAULT_ROW_POINTS = 15;
// A4 portrait body inside the 0.75in margins applyPrint applies.
const A4_BODY_POINTS = ((297 / 25.4) - 1.5) * 72;

const writeBankSheet = (workbook, company) => {
  const sheet = workbook.addWorksheet('Bank Accounts');
  sheet.columns = [{ width: 22 }, { width: 68 }];
  sheet.rowBreaks = [];
  let row = 1;
  let usedOnPage = 0;

  const place = (height, draw) => {
    if (draw) draw(row);
    sheet.getRow(row).height = height;
    usedOnPage += height;
    row += 1;
  };

  // A manual break sits above `row` (OOXML id is that row's zero-based index).
  // Break only between account blocks, and only when the next block would
  // cross the bottom of the page.
  const breakBefore = (blockHeight) => {
    if (usedOnPage > 0 && usedOnPage + blockHeight > A4_BODY_POINTS) {
      sheet.rowBreaks.push({ id: row - 1, min: 0, max: 16383, man: 1 });
      usedOnPage = 0;
    }
  };

  place(28, (at) => {
    sheet.mergeCells(at, 1, at, 2);
    const title = sheet.getCell(at, 1);
    setCellText(title, company.name);
    title.font = baseFont({ bold: true, size: 16, color: { argb: WHITE } });
    title.fill = fill(NAVY);
    title.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    applyFrame(sheet, at, 1, at, 2);
  });

  place(22, (at) => {
    sheet.mergeCells(at, 1, at, 2);
    const subtitle = sheet.getCell(at, 1);
    setCellText(subtitle, 'Bank accounts');
    subtitle.font = baseFont({ bold: true, size: 12, color: { argb: PRIMARY } });
    subtitle.alignment = { horizontal: 'left', vertical: 'middle' };
  });
  place(DEFAULT_ROW_POINTS);

  // Priced documents list every account the company has, not only the selected one.
  Object.values(company.banks || {}).forEach((bank) => {
    const entries = Object.entries(bank.accounts || {});
    entries.forEach(([currency, account], index) => {
      const lines = accountLines(currency, account).split('\n');
      const includeBankHeader = index === 0;
      const blockHeight = (includeBankHeader ? 22 : 0) + (lines.length * 20) + DEFAULT_ROW_POINTS;
      breakBefore(blockHeight);

      if (includeBankHeader) {
        place(22, (at) => {
          sheet.mergeCells(at, 1, at, 2);
          const bankHeader = sheet.getCell(at, 1);
          setCellText(bankHeader, bank.label || account.bank || 'Bank');
          bankHeader.font = baseFont({ bold: true, size: 12, color: { argb: WHITE } });
          bankHeader.fill = fill(PRIMARY);
          bankHeader.alignment = { horizontal: 'left', vertical: 'middle' };
          applyFrame(sheet, at, 1, at, 2);
        });
      }

      const start = row;
      lines.forEach((line, lineIndex) => {
        place(20, (at) => {
          const labelCell = sheet.getCell(at, 1);
          const valueCell = sheet.getCell(at, 2);
          if (lineIndex === 0) {
            sheet.mergeCells(at, 1, at, 2);
            setCellText(labelCell, line);
            labelCell.font = baseFont({ bold: true, size: 12, color: { argb: NAVY } });
            labelCell.fill = fill(BAND);
          } else {
            const splitAt = line.indexOf(':');
            setCellText(labelCell, splitAt >= 0 ? line.slice(0, splitAt + 1) : line);
            setCellText(valueCell, splitAt >= 0 ? line.slice(splitAt + 1).trim() : '');
            labelCell.font = baseFont({ bold: true, color: { argb: MUTED } });
            valueCell.font = baseFont();
            valueCell.alignment = { vertical: 'middle', wrapText: true };
          }
          labelCell.alignment = { vertical: 'middle', wrapText: true, horizontal: 'left' };
        });
      });
      applyFrame(sheet, start, 1, row - 1, 2);
      place(DEFAULT_ROW_POINTS);
    });
  });

  applyPrint(sheet, 1);
  // Fit the wide account table to the page width. A leftover scale would
  // make Excel ignore fitToWidth.
  sheet.pageSetup.scale = undefined;
  sheet.pageSetup.fitToPage = true;
  sheet.pageSetup.fitToWidth = 1;
  sheet.pageSetup.fitToHeight = 0;
  return sheet;
};

const rowHeightOf = (sheet, rowNumber) => sheet.getRow(rowNumber).height || DEFAULT_ROW_POINTS;

// Worksheet points that fit on one printed page after fit-to-width.
// Excel at 74% holds about 991.7pt and LibreOffice at 69% about 1059pt.
// Every manual page stays at 900pt, under both, so neither renderer adds a
// break of its own and the gap cannot grow after page 1. The repeated item
// header is already in the row stream on page 1; later pages count it again.
// A sheet inside 960pt still fits on one Excel page with room to spare, so
// it gets no manual break. 992pt does not: that lands on the measured page
// and the terms row spills.
const PACKING_PAGE_POINTS = 900;
const PACKING_ONE_PAGE_POINTS = 960;

const writePackingPageBreaks = (
  sheet,
  blockStart,
  blockEnd,
  headerRow,
  lastContentRow,
  packageTitleRow,
  packageKeepEnd
) => {
  if (!lastContentRow || lastContentRow < 1) return;
  const repeat = headerRow && headerRow >= 1 && headerRow <= lastContentRow
    ? rowHeightOf(sheet, headerRow)
    : 0;
  const hasBlock = blockStart && blockEnd && blockEnd >= blockStart && blockStart <= lastContentRow;
  const blockStop = hasBlock ? Math.min(blockEnd, lastContentRow) : 0;
  const hasPackageTitle = packageTitleRow && packageTitleRow >= 1 && packageTitleRow <= lastContentRow;
  const packageStop = hasPackageTitle
    ? Math.min(Math.max(packageKeepEnd || packageTitleRow, packageTitleRow), lastContentRow)
    : 0;
  let blockHeight = 0;
  let packageHeight = 0;
  let total = 0;
  for (let row = 1; row <= lastContentRow; row += 1) {
    const height = rowHeightOf(sheet, row);
    total += height;
    if (hasBlock && row >= blockStart && row <= blockStop) blockHeight += height;
    if (hasPackageTitle && row >= packageTitleRow && row <= packageStop) packageHeight += height;
  }
  sheet.rowBreaks = [];
  if (total <= PACKING_ONE_PAGE_POINTS) return;
  let used = 0;
  let page = 0;
  const pageLimit = () => PACKING_PAGE_POINTS - (page > 0 ? repeat : 0);
  const breakBefore = (row) => {
    if (row <= 1 || row > lastContentRow) return;
    sheet.rowBreaks.push({ id: row - 1, min: 0, max: 16383, man: 1 });
    used = 0;
    page += 1;
  };
  for (let row = 1; row <= lastContentRow; row += 1) {
    const height = rowHeightOf(sheet, row);
    const atBlock = hasBlock && row === blockStart;
    const insideBlock = hasBlock && row > blockStart && row <= blockStop;
    const atPackageTitle = hasPackageTitle && row === packageTitleRow;
    const insidePackage = hasPackageTitle && row > packageTitleRow && row <= packageStop;
    if (atPackageTitle) {
      // The title stays with the column header and the first package row.
      // If that group would leave the title at the bottom of the page, break
      // before the title.
      if (used > 0 && used + packageHeight > pageLimit()) breakBefore(row);
    } else if (atBlock) {
      if (used > 0 && used + blockHeight > pageLimit()) breakBefore(row);
    } else if (!insideBlock && !insidePackage && used > 0 && used + height > pageLimit()) {
      breakBefore(row);
    }
    used += height;
  }
};

// Landscape A4 body inside the 0.7in / 0.75in margins. Column pixels are the
// same 96dpi grid the packing-list scale was measured against.
const LANDSCAPE_WIDTH_PX = ((297 / 25.4) - 1.4) * 96;
const LANDSCAPE_HEIGHT_PT = ((210 / 25.4) - 1.5) * 72;
// About 58%. The edited 3-item invoice, including the logo row, still fits
// on one landscape page at this scale. Shorter sheets do too.
const WIDE_ONE_PAGE_SCALE = 0.58;
// Multi-page 8-column sheets use the owner's fixed print scale instead of
// shrinking only to the page width (about 100% for these columns).
const WIDE_PRINT_SCALE = 0.75;

const sheetWidthPx = (widths) => widths.reduce((sum, width) => sum + columnPixels(width), 0);

const sumRowHeights = (sheet, from, to) => {
  let total = 0;
  for (let row = from; row <= to; row += 1) total += rowHeightOf(sheet, row);
  return total;
};

const wideOnePageScale = (sheet, lastRow, widths) => {
  const total = sumRowHeights(sheet, 1, lastRow);
  if (total <= 0) return 1;
  const widthScale = LANDSCAPE_WIDTH_PX / sheetWidthPx(widths);
  return Math.min(widthScale, LANDSCAPE_HEIGHT_PT / total);
};

const closeItemPage = (sheet, row, cols) => {
  for (let col = 1; col <= cols; col += 1) {
    const cell = sheet.getCell(row, col);
    const current = cell.border || {};
    cell.border = {
      top: current.top,
      left: current.left,
      right: current.right,
      bottom: BOX_EDGE
    };
  }
};

// Pagination for a multi-page 8-column sheet printed at WIDE_PRINT_SCALE.
// The item header stays with the first item. The last item stays with the
// totals, terms, notes and bank, so a repeated item header is never left
// above a page that has no item row under it.
const writeWidePageBreaks = (sheet, headerRow, lastItemRow, lastContentRow, cols) => {
  if (!lastContentRow || lastContentRow < 2) return true;
  const pagePoints = LANDSCAPE_HEIGHT_PT / WIDE_PRINT_SCALE;
  const headerHeight = headerRow ? rowHeightOf(sheet, headerRow) : 0;
  const hasItem = Boolean(lastItemRow && headerRow && lastItemRow > headerRow);
  const firstItemRow = hasItem ? headerRow + 1 : 0;
  let keepStart = 0;
  let keepEnd = 0;
  if (hasItem) {
    keepStart = lastItemRow;
    // Through the end of the sheet, not only the total rows. A break after
    // the last item would open a page of notes and bank under the repeated
    // item header.
    keepEnd = lastContentRow;
  }
  sheet.rowBreaks = [];
  let used = 0;
  let page = 0;
  const pageLimit = () => {
    if (page === 0) return pagePoints;
    const start = sheet.rowBreaks[sheet.rowBreaks.length - 1].id + 1;
    // A repeated item header takes a row only on a page that continues the
    // items. A page that begins on the header, or on terms after the items
    // with no item page before it, does not.
    if (start === headerRow) return pagePoints;
    const itemPage = sheet.rowBreaks.some((brk) => {
      const at = brk.id + 1;
      return at > headerRow && at <= lastItemRow;
    });
    if (!itemPage) return pagePoints;
    return pagePoints - headerHeight;
  };
  const breakBefore = (row) => {
    if (row <= 1 || row > lastContentRow) return;
    if (sheet.rowBreaks.some((brk) => brk.id === row - 1)) return;
    sheet.rowBreaks.push({ id: row - 1, min: 0, max: 16383, man: 1 });
    if (hasItem && row - 1 > headerRow && row - 1 <= lastItemRow) {
      closeItemPage(sheet, row - 1, cols);
    }
    used = 0;
    page += 1;
  };
  for (let row = 1; row <= lastContentRow; row += 1) {
    const height = rowHeightOf(sheet, row);
    const atHeader = Boolean(firstItemRow && row === headerRow);
    const atFirstItem = Boolean(firstItemRow && row === firstItemRow);
    const atKeep = Boolean(keepStart && row === keepStart && !atFirstItem);
    const insideKeep = Boolean(keepStart && row > keepStart && row <= keepEnd);
    if (atHeader) {
      // One-item sheets keep the header, that item and the totals together.
      // Longer sheets keep the header with the first item only.
      const groupEnd = keepStart === firstItemRow ? keepEnd : firstItemRow;
      const group = sumRowHeights(sheet, row, groupEnd);
      if (used > 0 && used + group > pageLimit()) breakBefore(row);
    } else if (atFirstItem) {
      // Never break between the item header and the first item row.
    } else if (atKeep) {
      const keepHeight = sumRowHeights(sheet, keepStart, keepEnd);
      if (used > 0 && used + keepHeight > pageLimit()) breakBefore(row);
    } else if (!insideKeep && used > 0 && used + height > pageLimit()) {
      breakBefore(row);
    }
    used += height;
  }
  // A page of only totals, terms, notes or bank would repeat the item header
  // with nothing under it. Move that final break to the last item row so the
  // item, the totals and the notes share the page.
  if (hasItem && sheet.rowBreaks.some((brk) => brk.id + 1 > lastItemRow)) {
    sheet.rowBreaks = sheet.rowBreaks.filter((brk) => brk.id + 1 <= lastItemRow);
    if (!sheet.rowBreaks.some((brk) => brk.id + 1 === lastItemRow) && lastItemRow > 1) {
      sheet.rowBreaks.push({ id: lastItemRow - 1, min: 0, max: 16383, man: 1 });
      if (lastItemRow - 1 > headerRow) closeItemPage(sheet, lastItemRow - 1, cols);
    }
    sheet.rowBreaks.sort((left, right) => left.id - right.id);
  }
  const starts = sheet.rowBreaks.map((brk) => brk.id + 1);
  const itemPage = starts.some((start) => start > headerRow && start <= lastItemRow);
  const tailOnly = starts.some((start) => !hasItem || start > (keepEnd || lastItemRow));
  // The header row is already on a page that starts there, so a repeated
  // title would print it twice. A terms/notes/bank page does not need it
  // when no later page is continuing the items.
  if (starts.includes(headerRow) && !itemPage) return false;
  if (!itemPage && tailOnly) return false;
  return true;
};

const styleHeaderCell = (cell) => {
  cell.font = baseFont({ bold: true, size: 10, color: { argb: WHITE } });
  cell.fill = fill(BRAND);
  cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
};

const writeTotalRow = (sheet, row, label, amount, currency, prominent, discount, cols) => {
  const valueCol = cols;
  sheet.mergeCells(row, 1, row, valueCol - 1);
  const labelCell = sheet.getCell(row, 1);
  const valueCell = sheet.getCell(row, valueCol);
  setCellText(labelCell, label);
  valueCell.value = roundMoney(amount);
  valueCell.numFmt = currencyFormat(currency);
  const color = discount ? 'FFD73027' : BRAND;
  const totalFont = baseFont({ bold: true, size: prominent ? 12 : 11, color: { argb: color } });
  labelCell.font = totalFont;
  valueCell.font = totalFont;
  labelCell.alignment = { horizontal: 'right', vertical: 'middle', wrapText: true, indent: 1 };
  valueCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  fillRow(sheet, row, cols, WHITE);
  applyBox(sheet, row, 1, row, valueCol - 1);
  applyBox(sheet, row, valueCol, row, valueCol);
  sheet.getRow(row).height = 28;
};

const writeItems = (sheet, model, startRow, layout) => {
  const offerType = normalizeOfferType(model.offerType);
  const packing = offerType === 'PACKING LIST';
  const { cols, widths, wide } = layout;
  const headers = packing
    ? ['ITEM', 'DESCRIPTION', 'QTY', `WEIGHT (${model.weightUnit || 'KG'})`, 'PACKAGE']
    : wide
      ? ['ITEM', 'DESCRIPTION', 'PART NO', 'CONDITION', 'REMARKS', 'QTY', 'UNIT PRICE', 'TOTAL PRICE']
      : ['ITEM', 'DESCRIPTION', 'QTY', 'REMARKS', 'AVAILABILITY'];
  const headerRow = startRow;
  headers.forEach((header, index) => {
    const cell = sheet.getCell(headerRow, index + 1);
    setCellText(cell, header);
    styleHeaderCell(cell);
  });
  sheet.getRow(headerRow).height = 24;

  let row = headerRow + 1;
  model.items.forEach((item) => {
    const description = itemDescription(item, offerType);
    const unitPrice = storedUnitPrice(item.unitPrice);
    const remarksText = visibleCellText(item.remarks || '');
    const packageText = visibleCellText(item.packageNumber).trim() || 'Box 1';
    const availabilityText = visibleCellText(item.availability || '');
    const partText = visibleCellText(item.partNo || '');
    const conditionText = visibleCellText(item.condition || '');
    const values = wide
      ? [
        row - headerRow,
        description,
        partText,
        conditionText,
        remarksText,
        item.qty,
        model.showPrices ? unitPrice : null,
        model.showPrices ? roundMoney(item.lineTotal) : null
      ]
      : [
        row - headerRow,
        description,
        item.qty,
        packing ? roundMoney(item.weight) : remarksText,
        packing ? packageText : availabilityText
      ];
    values.forEach((value, col) => {
      const cell = sheet.getCell(row, col + 1);
      if (typeof value === 'number') cell.value = value;
      else setCellText(cell, value === '' || value == null ? null : value);
      cell.font = baseFont({ size: 10 });
      const indent = wide && (col === 1 || col === 4) ? 1 : 0;
      let horizontal = 'center';
      if (!wide && col === 1) horizontal = 'left';
      else if (!wide && col >= 3 && model.showPrices) horizontal = 'right';
      else if (wide && (col === 1 || col === 4)) horizontal = 'left';
      const alignment = { vertical: 'middle', wrapText: true, horizontal };
      if (indent) alignment.indent = indent;
      cell.alignment = alignment;
      cell.fill = fill(WHITE);
    });
    if (wide) {
      sheet.getCell(row, 6).numFmt = quantityFormat(item.qty);
      if (model.showPrices) {
        const currency = offerType === 'PURCHASE ORDER' ? item.currency : model.currency;
        sheet.getCell(row, 7).numFmt = unitPriceFormat(currency, item.unitPrice);
        sheet.getCell(row, 8).numFmt = currencyFormat(currency);
      }
    } else {
      sheet.getCell(row, 3).numFmt = quantityFormat(item.qty);
      if (packing) sheet.getCell(row, 4).numFmt = '#,##0.00';
    }
    let lineCount = 1;
    const rowHeight = values.reduce((tallest, value, col) => {
      if (typeof value === 'number' || value == null || value === '') return Math.max(tallest, 22);
      const text = String(value);
      const lines = wrappedLineCount(text, 10, spanTextPixels(col + 1, col + 1, widths));
      if (lines > lineCount) lineCount = lines;
      return Math.max(tallest, textHeight(text, col + 1, col + 1, 10, 22, widths));
    }, 22);
    // A 1-line item keeps its natural height (about 20–24pt). The edited
    // invoice uses 48pt only for a 2- or 3-line item. Taller text still grows.
    const itemHeight = wide && lineCount >= 2 && lineCount <= 3 ? Math.max(rowHeight, 48) : rowHeight;
    sheet.getRow(row).height = itemHeight;
    row += 1;
  });

  if (offerType === 'PURCHASE ORDER') {
    (model.poTotals || []).forEach((total, index) => {
      writeTotalRow(
        sheet,
        row,
        `TOTAL (${total.currency}):`,
        total.amount,
        total.currency,
        index === (model.poTotals.length - 1) && !model.discount,
        false,
        cols
      );
      row += 1;
    });
  } else if (model.showPrices) {
    const prominent = !model.discount;
    writeTotalRow(sheet, row, `TOTAL (${model.currency}):`, model.subtotal, model.currency, prominent, false, cols);
    row += 1;
    if (model.discount) {
      // Same displayed discount the screen and PDF print. The amount-type
      // label uses this figure so its money text cannot disagree with the cell.
      const shownDiscount = roundDisplayedMoney(model.discount.amount);
      const detail = model.discount.type === 'percentage'
        ? `${model.discount.value}%`
        : `${model.currency} ${shownDiscount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      const rawFinal = model.finalTotal != null && Number.isFinite(Number(model.finalTotal))
        ? Number(model.finalTotal)
        : Number(model.subtotal) - Number(model.discount.amount || 0);
      const finalAmount = roundDisplayedMoney(rawFinal);
      writeTotalRow(sheet, row, `${model.discount.label} (${detail}):`, shownDiscount === 0 ? 0 : -shownDiscount, model.currency, false, true, cols);
      row += 1;
      writeTotalRow(sheet, row, `FINAL TOTAL (${model.currency}):`, finalAmount, model.currency, true, false, cols);
      row += 1;
    }
  }

  const lastItemRow = model.items.length ? headerRow + model.items.length : headerRow;
  const totalEnd = row - 1 > lastItemRow ? row - 1 : null;
  applyItemGrid(sheet, headerRow, Math.max(headerRow, lastItemRow), cols);
  headers.forEach((_, index) => styleHeaderCell(sheet.getCell(headerRow, index + 1)));
  return { headerRow, nextRow: row, lastItemRow, totalEnd };
};

const notesFor = (model) => {
  const offerType = normalizeOfferType(model.offerType);
  const notes = [];
  if (offerType === 'OFFER') {
    const until = validUntil(model.date);
    notes.push(`This offer is valid for 2 weeks from the offer date.${until ? ` (valid until ${until})` : ''}`);
  }
  const priced = PRICED_TYPES.includes(offerType);
  if (priced) {
    notes.push('Our company shall not be held liable for any payments made to third parties or to accounts at banks or branches other than those explicitly specified by our company.');
    notes.push('If you receive any communication regarding alternative bank account details, please contact us immediately by phone or WhatsApp to verify the authenticity of such instructions.');
  }
  return notes;
};

export const buildDocumentWorkbook = (model) => {
  const offerType = normalizeOfferType(model.offerType);
  const priced = PRICED_TYPES.includes(offerType);
  const showBank = priced && Boolean(model.showBankOnDocument) && Boolean(model.bankDetails);
  const workbook = new ExcelJS.Workbook();
  const companyName = model.company?.name || 'Marine Offer Builder';
  workbook.creator = companyName;
  workbook.lastModifiedBy = companyName;
  workbook.created = new Date();
  const layout = layoutFor(offerType);
  const sheet = workbook.addWorksheet(sheetNameFor(offerType));
  sheet.columns = layout.widths.map((width) => ({ width }));
  const block = layout.wide
    ? {
      fillColor: WHITE,
      font: baseFont({ size: 10 }),
      vertical: 'top',
      indent: 1,
      span: layout.cols,
      widths: layout.widths
    }
    : {
      fillColor: WHITE,
      font: baseFont({ size: 11 }),
      vertical: 'middle',
      indent: 0,
      span: layout.cols,
      widths: layout.widths
    };

  let row = 1;
  // Every document, wide or compact, keeps the logo in one merged cell
  // across the sheet. The picture is centred in that area.
  sheet.mergeCells(row, 1, row, layout.cols);
  if (model.logoBase64) {
    placeLogo(workbook, sheet, model.logoBase64, layout.widths);
  } else {
    sheet.getRow(row).height = 18;
  }
  row += 1;

  sheet.mergeCells(row, 1, row, layout.cols);
  const companyCell = sheet.getCell(row, 1);
  const companyText = `${model.company.name}\n${model.company.address}\n${model.company.email}`;
  setCellText(companyCell, {
    richText: [
      { font: baseFont({ bold: true, size: 13, color: { argb: BRAND } }), text: `${model.company.name}\n` },
      { font: baseFont({ size: 10, color: { argb: BRAND } }), text: `${model.company.address}\n` },
      { font: baseFont({ size: 10, underline: true, color: { argb: BRAND } }), text: model.company.email }
    ]
  });
  companyCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  fillRow(sheet, row, layout.cols, WHITE);
  applyBox(sheet, row, 1, row, layout.cols);
  sheet.getRow(row).height = textHeight(companyText, 1, layout.cols, 11, 52, layout.widths);
  row += 1;

  const title = `${visibleCellText(offerType)} // ${visibleCellText(model.offerNumber || '')}`;
  row = box(sheet, row, title, {
    fillColor: BRAND,
    font: baseFont({ bold: true, size: 16, color: { argb: WHITE } }),
    horizontal: 'center',
    span: layout.cols,
    widths: layout.widths,
    framed: false,
    minHeight: layout.wide ? 28 : 22
  });

  const labelSpan = layout.wide ? 6 : 4;
  const metaStart = labelSpan + 1;
  const customerText = customerBlock(model.customer, offerType);
  sheet.mergeCells(row, 1, row, labelSpan);
  if (metaStart < layout.cols) sheet.mergeCells(row, metaStart, row, layout.cols);
  const customerCell = sheet.getCell(row, 1);
  setCellText(customerCell, customerText);
  customerCell.font = baseFont();
  customerCell.alignment = {
    horizontal: 'left',
    vertical: layout.wide ? 'middle' : 'top',
    wrapText: true,
    ...(layout.wide ? { indent: 1 } : {})
  };
  const dateCell = sheet.getCell(row, metaStart);
  setCellText(dateCell, `DATE: ${formatDocumentDate(visibleCellText(model.date))}`);
  dateCell.font = baseFont({ bold: true, size: layout.wide ? 11 : 12, color: { argb: BRAND } });
  dateCell.alignment = {
    horizontal: layout.wide ? 'right' : 'center',
    vertical: 'middle',
    wrapText: true,
    ...(layout.wide ? { indent: 1 } : {})
  };
  fillRow(sheet, row, layout.cols, WHITE);
  applyOpenRow(sheet, row, metaStart);
  const customerHeight = textHeight(customerText, 1, labelSpan, 11, layout.wide ? 68 : 48, layout.widths);
  sheet.getRow(row).height = layout.wide ? customerHeight : Math.max(customerHeight, 36);
  row += 1;

  sheet.mergeCells(row, 1, row, labelSpan);
  if (metaStart < layout.cols) sheet.mergeCells(row, metaStart, row, layout.cols);
  const forText = visibleCellText(model.description || '');
  const forCell = sheet.getCell(row, 1);
  setCellText(forCell, `FOR: ${forText}`);
  forCell.font = baseFont({ bold: true });
  forCell.alignment = {
    horizontal: 'left',
    vertical: 'middle',
    wrapText: true,
    ...(layout.wide ? { indent: 1 } : {})
  };
  const serialCell = sheet.getCell(row, metaStart);
  const serialText = visibleCellText(String(model.engineSerialNumber || '')).trim() || '--';
  setCellText(serialCell, `SERIAL NO: ${serialText}`);
  serialCell.font = baseFont({ bold: true, color: { argb: BRAND } });
  serialCell.alignment = {
    horizontal: layout.wide ? 'right' : 'center',
    vertical: 'middle',
    wrapText: true,
    ...(layout.wide ? { indent: 1 } : {})
  };
  fillRow(sheet, row, layout.cols, WHITE);
  applyOpenRow(sheet, row, metaStart);
  const forHeight = textHeight(`FOR: ${forText}`, 1, labelSpan, 11, layout.wide ? 28 : 32, layout.widths);
  const serialHeight = textHeight(
    `SERIAL NO: ${serialText}`,
    metaStart,
    layout.cols,
    layout.wide ? 11 : 12,
    layout.wide ? 28 : 28,
    layout.widths
  );
  sheet.getRow(row).height = Math.max(forHeight, serialHeight, layout.wide ? 28 : 28);
  row += 1;

  const items = writeItems(sheet, model, row, layout);
  row = items.nextRow;

  let weightBlock = null;
  let packageTitleRow = null;
  let packageKeepEnd = null;
  if (offerType === 'PACKING LIST') {
    packageTitleRow = row;
    row = box(sheet, row, 'PACKAGE SUMMARY', {
      fillColor: BRAND,
      font: baseFont({ bold: true, size: 12, color: { argb: WHITE } }),
      horizontal: 'left',
      span: layout.cols,
      widths: layout.widths
    });
    const summaryHeader = row;
    ['Package', 'Type', 'Dimensions (cm)', `Box Weight (${model.weightUnit || 'KG'})`, `Total Weight (${model.weightUnit || 'KG'})`]
      .forEach((header, index) => {
        const cell = sheet.getCell(summaryHeader, index + 1);
        setCellText(cell, header);
        styleHeaderCell(cell);
      });
    sheet.getRow(summaryHeader).height = 24;
    row += 1;
    // Title, column header, and the first package row stay on one page.
    packageKeepEnd = (model.packages || []).length ? row : summaryHeader;
    (model.packages || []).forEach((pkg) => {
      const values = [
        visibleCellText(pkg.number),
        visibleCellText(pkg.type),
        visibleCellText(pkg.dimensions),
        roundMoney(pkg.boxWeight || 0),
        roundMoney(pkg.totalWeight || 0)
      ];
      values.forEach((value, col) => {
        const cell = sheet.getCell(row, col + 1);
        if (typeof value === 'number') cell.value = value;
        else setCellText(cell, value == null || value === '' ? null : value);
        cell.font = baseFont({ size: 10 });
        cell.alignment = { vertical: 'middle', wrapText: true, horizontal: col >= 3 ? 'right' : 'left' };
        cell.fill = fill(WHITE);
      });
      sheet.getCell(row, 4).numFmt = '#,##0.00';
      sheet.getCell(row, 5).numFmt = '#,##0.00';
      const packageHeight = values.reduce((tallest, value, col) => {
        if (typeof value === 'number') return Math.max(tallest, 22);
        return Math.max(tallest, textHeight(value == null ? '' : String(value), col + 1, col + 1, 10, 22, layout.widths));
      }, 22);
      sheet.getRow(row).height = packageHeight;
      row += 1;
    });
    applyItemGrid(sheet, summaryHeader, Math.max(summaryHeader, row - 1), layout.cols);
    ['Package', 'Type', 'Dimensions (cm)', `Box Weight (${model.weightUnit || 'KG'})`, `Total Weight (${model.weightUnit || 'KG'})`]
      .forEach((_, index) => styleHeaderCell(sheet.getCell(summaryHeader, index + 1)));

    const weightStart = row;
    row = box(sheet, row, 'Weight Summary', {
      fillColor: WHITE,
      font: baseFont({ bold: true, size: 12, color: { argb: BRAND } }),
      span: layout.cols,
      widths: layout.widths
    });
    const summary = model.weightSummary || {};
    const summaryStart = row;
    [
      ['Number of packages:', summary.packageCount || 0, '0', false],
      ['Total items weight:', summary.itemsWeight || 0, '#,##0.00', false],
      ['Total packaging weight:', summary.packagingWeight || 0, '#,##0.00', false],
      ['Total gross weight:', summary.grossWeight || 0, '#,##0.00', true]
    ].forEach(([label, value, numFmt, prominent]) => {
      sheet.mergeCells(row, 1, row, 3);
      const labelCell = sheet.getCell(row, 1);
      const valueCell = sheet.getCell(row, 4);
      const unitCell = sheet.getCell(row, 5);
      setCellText(labelCell, label);
      valueCell.value = label === 'Number of packages:' ? value : roundMoney(value);
      valueCell.numFmt = numFmt;
      const summaryFont = baseFont({ bold: prominent, color: { argb: prominent ? BRAND : TEXT } });
      labelCell.font = summaryFont;
      valueCell.font = summaryFont;
      labelCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
      valueCell.alignment = { horizontal: 'right', vertical: 'middle' };
      if (label !== 'Number of packages:') {
        setCellText(unitCell, model.weightUnit || 'KG');
        unitCell.font = baseFont({ bold: prominent, color: { argb: MUTED } });
        unitCell.alignment = { horizontal: 'left', vertical: 'middle' };
      }
      fillRow(sheet, row, layout.cols, WHITE);
      sheet.getRow(row).height = 22;
      row += 1;
    });
    applyItemGrid(sheet, summaryStart, row - 1, layout.cols);
    weightBlock = { start: weightStart, end: row - 1 };

    if (model.shipping) {
      const lines = ['SHIPPING INFORMATION', `PICKUP ADDRESS: ${visibleCellText(model.shipping.pickup).trim() || 'Not specified'}`];
      if (model.shipping.consignee != null) {
        lines.push(`CONSIGNEE ADDRESS: ${visibleCellText(model.shipping.consignee).trim() || 'Not specified'}`);
      }
      lines.push(`DELIVERY ADDRESS: ${visibleCellText(model.shipping.delivery).trim() || 'Not specified'}`);
      row = box(sheet, row, lines.join('\n'), block);
    }
  }

  if (model.offerAddresses) {
    row = box(sheet, row, [
      'SHIPPING ADDRESSES',
      `PICKUP ADDRESS: ${visibleCellText(model.offerAddresses.pickup).trim() || 'Not specified'}`,
      `SHIPPING ADDRESS: ${visibleCellText(model.offerAddresses.shipping).trim() || 'Not specified'}`
    ].join('\n'), block);
  }

  row = box(sheet, row, termsText(model.terms, offerType), block);

  const remarks = visibleCellText(model.remarks || '').trim();
  if (remarks) {
    row = box(sheet, row, `Remarks: ${remarks}`, {
      ...block,
      fillColor: WHITE,
      font: baseFont({ bold: true, size: 10, color: { argb: BRAND } }),
      vertical: 'top',
      indent: 1
    });
  }

  const notes = notesFor(model);
  if (notes.length) {
    row = box(sheet, row, `NOTES:\n${notes.join('\n')}`, {
      ...block,
      fillColor: WHITE,
      font: baseFont({ size: 9, italic: true }),
      vertical: 'top',
      indent: 1,
      // The edited invoice's notes row is 66pt. Longer text still grows past that.
      minHeight: layout.wide ? 66 : 22
    });
  }

  if (showBank) {
    const bankText = accountLines(model.currency, model.bankDetails);
    row = box(sheet, row, bankText, {
      ...block,
      fillColor: WHITE,
      font: baseFont({ size: 10 }),
      vertical: 'top',
      indent: 1
    });
    const bankLines = bankText.split('\n');
    setCellText(sheet.getCell(row - 1, 1), {
      richText: bankLines.map((line, index) => ({
        font: baseFont({ size: 10, bold: index === 0 }),
        text: index === 0 ? line : `\n${line}`
      }))
    });
  }

  let lastContentRow = 1;
  sheet.eachRow({ includeEmpty: false }, (_sheetRow, rowNumber) => {
    lastContentRow = rowNumber;
  });
  let repeatHeader = true;
  let fitToHeight = 0;
  let printScale;
  if (offerType === 'PACKING LIST') {
    writePackingPageBreaks(
      sheet,
      weightBlock && weightBlock.start,
      weightBlock && weightBlock.end,
      items.headerRow,
      lastContentRow,
      packageTitleRow,
      packageKeepEnd
    );
  } else if (layout.wide) {
    const scale = wideOnePageScale(sheet, lastContentRow, layout.widths);
    if (scale >= WIDE_ONE_PAGE_SCALE) {
      // Still readable at about 58% or more, including the owner's 3-item
      // invoice with the logo. Shrink the whole sheet onto one page.
      sheet.rowBreaks = [];
      fitToHeight = 1;
    } else {
      printScale = Math.round(WIDE_PRINT_SCALE * 100);
      repeatHeader = writeWidePageBreaks(
        sheet,
        items.headerRow,
        items.lastItemRow,
        lastContentRow,
        layout.cols
      );
    }
  }
  applyPrint(sheet, items.headerRow, {
    orientation: layout.wide ? 'landscape' : 'portrait',
    // K saved the invoice at 75%. Inquiry and packing list keep the default view.
    zoom: layout.wide ? 75 : undefined,
    fitToHeight,
    repeatHeader,
    scale: printScale
  });
  // Stop the print range at the last cell, including the logo row. A range
  // that runs on, or a break after that row, prints a trailing page with
  // only the repeated item header.
  const lastColumn = layout.cols === 8 ? 'H' : 'E';
  sheet.pageSetup.printArea = `A$1:${lastColumn}$${lastContentRow}`;
  if (showBank) writeBankSheet(workbook, model.company);
  return workbook;
};
